import { Box, Button, Container, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Link } from "react-router-dom";
import { colors } from "./tokens";

// A full-width band with a centred container. `tone` picks the background.
export function Section({ tone = "paper", py = { xs: 8, md: 12 }, id, children, maxWidth = "lg", sx }) {
  const backgrounds = {
    paper: colors.paper,
    warm: colors.paperWarm,
    navy: colors.navy,
    deep: colors.navyDeep,
  };
  const dark = tone === "navy" || tone === "deep";
  return (
    <Box
      component="section"
      id={id}
      sx={{ backgroundColor: backgrounds[tone], color: dark ? colors.onDark : colors.ink, py, ...sx }}
    >
      <Container maxWidth={maxWidth}>{children}</Container>
    </Box>
  );
}

export function Eyebrow({ children, dark = false, sx }) {
  return (
    <Typography
      component="p"
      sx={{
        fontSize: "0.78rem",
        fontWeight: 700,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: dark ? colors.orange : colors.orangeDark,
        mb: 2,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

// Eyebrow + heading + optional lead paragraph, left aligned.
export function SectionHeader({ eyebrow, title, lead, dark = false, maxWidth = 720, sx }) {
  return (
    <Box sx={{ maxWidth, mb: { xs: 5, md: 7 }, ...sx }}>
      {eyebrow && <Eyebrow dark={dark}>{eyebrow}</Eyebrow>}
      <Typography variant="h2" component="h2" sx={{ color: dark ? colors.onDark : colors.ink }}>
        {title}
      </Typography>
      {lead && (
        <Typography
          sx={{ mt: 2.5, fontSize: { xs: "1.05rem", md: "1.15rem" }, lineHeight: 1.65, color: dark ? colors.onDarkMuted : colors.body }}
        >
          {lead}
        </Typography>
      )}
    </Box>
  );
}

// Internal routes go through react-router; anything else opens as a normal link.
function linkProps(to) {
  if (!to) return {};
  if (/^(https?:|mailto:|tel:)/.test(to)) {
    const external = to.startsWith("http");
    return { component: "a", href: to, ...(external ? { target: "_blank", rel: "noopener noreferrer" } : {}) };
  }
  return { component: Link, to };
}

export function PrimaryButton({ to, children, dark = false, sx, ...rest }) {
  return (
    <Button
      {...linkProps(to)}
      variant="contained"
      disableElevation
      endIcon={<ArrowForwardIcon sx={{ fontSize: "1.1rem !important" }} />}
      sx={{
        backgroundColor: dark ? colors.orange : colors.navy,
        color: dark ? colors.navy : colors.onDark,
        "&:hover": { backgroundColor: dark ? "#F3AE4A" : colors.navyMid },
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Button>
  );
}

export function SecondaryButton({ to, children, dark = false, sx, ...rest }) {
  return (
    <Button
      {...linkProps(to)}
      variant="outlined"
      sx={{
        color: dark ? colors.onDark : colors.navy,
        borderColor: dark ? "rgba(255,255,255,0.35)" : colors.line,
        "&:hover": {
          borderColor: dark ? colors.onDark : colors.navy,
          backgroundColor: dark ? "rgba(255,255,255,0.06)" : "rgba(20,33,61,0.04)",
        },
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Button>
  );
}

// Text link with an arrow, for "read more" style navigation.
export function ArrowLink({ to, children, dark = false, sx }) {
  return (
    <Box
      {...linkProps(to)}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        fontWeight: 600,
        fontSize: "0.95rem",
        color: dark ? colors.onDark : colors.navy,
        textDecoration: "none",
        borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.35)" : colors.line}`,
        pb: 0.25,
        transition: "border-color .2s, gap .2s",
        "&:hover": { borderColor: dark ? colors.orange : colors.navy, gap: 1.25 },
        ...sx,
      }}
    >
      {children}
      <ArrowForwardIcon sx={{ fontSize: "1rem" }} />
    </Box>
  );
}
